import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  Button,
  Box,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { useAuth } from "../../contexts/auth.context";
import { useProjectStore } from "../../stores/project.store";
import { CreateProjectData, Project } from "../../services/projects.service";

export interface ProjectFormProps {
  mode: "create" | "edit";
  project?: Project; // Only required for edit mode
  onSubmit: (project: any) => void;
  onCancel: () => void;
  loading?: boolean;
}

const ProjectForm = ({
  mode,
  project,
  onSubmit,
  onCancel,
  loading = false,
}: ProjectFormProps) => {
  const { auth } = useAuth();
  const { createProject, updateProject } = useProjectStore();
  const [formData, setFormData] = useState<CreateProjectData>({
    project_name: project?.project_name || "",
    project_description: project?.project_description || "",
    data_type: project?.data_type || "",
  });
  const [error, setError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Update form data when project changes (for edit mode)
  useEffect(() => {
    if (mode === "edit" && project) {
      setFormData({
        project_name: project.project_name,
        project_description: project.project_description,
        data_type: project.data_type,
      });
    }
  }, [mode, project]);

  const handleInputChange =
    (field: keyof CreateProjectData) =>
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any,
    ) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
      if (error) setError("");
    };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!formData.project_name.trim() || !formData.data_type) {
      setError("Project Name and Data Type are required.");
      return;
    }

    if (!auth?.accessToken) {
      setError("Authentication required.");
      return;
    }

    try {
      setIsSubmitting(true);
      setError("");

      let result: Project | null = null;

      if (mode === "create") {
        result = await createProject(auth, formData);
      } else if (mode === "edit" && project) {
        result = await updateProject(auth, project.id, formData);
      }

      if (result) {
        onSubmit(result);

        // Reset form for create mode
        if (mode === "create") {
          setFormData({
            project_name: "",
            project_description: "",
            data_type: "",
          });
        }
      } else {
        // If result is null, it means there was an error in the store
        // The error should be available in the store's error state
        const { error: storeError } = useProjectStore.getState();
        if (storeError) {
          setError(storeError);
        } else {
          setError(
            `Failed to ${mode === "create" ? "create" : "update"} project. Please try again.`,
          );
        }
      }
    } catch (err: any) {
      console.error(
        `Error ${mode === "create" ? "creating" : "updating"} project:`,
        err,
      );
      setError(
        err.message ||
          `Failed to ${mode === "create" ? "create" : "update"} project. Please try again.`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const dataTypeOptions = [
    { value: "metabolomics", label: "Metabolomics" },
    { value: "single_cell_data", label: "Single cell data" },
    { value: "other", label: "Others" },
  ];

  const isEditMode = mode === "edit";
  const submitButtonText = isSubmitting
    ? isEditMode
      ? "Updating..."
      : "Creating..."
    : isEditMode
      ? "Update Project"
      : "Create Project";

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <TextField
          inputRef={inputRef}
          label="Project Name"
          value={formData.project_name}
          onChange={handleInputChange("project_name")}
          required
          fullWidth
          variant="outlined"
          disabled={isSubmitting || loading}
          sx={{
            mt: 2,
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#f5f5f5",
              "&:hover": {
                backgroundColor: "#eeeeee",
              },
              "&.Mui-focused": {
                backgroundColor: "#ffffff",
              },
            },
          }}
        />

        <TextField
          label="Project Description"
          value={formData.project_description}
          onChange={handleInputChange("project_description")}
          multiline
          rows={3}
          fullWidth
          variant="outlined"
          disabled={isSubmitting || loading}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#f5f5f5",
              "&:hover": {
                backgroundColor: "#eeeeee",
              },
              "&.Mui-focused": {
                backgroundColor: "#ffffff",
              },
            },
          }}
        />

        <FormControl fullWidth required>
          <InputLabel>Data Type</InputLabel>
          <Select
            value={formData.data_type}
            onChange={handleInputChange("data_type")}
            label="Data Type"
            disabled={isSubmitting || loading}
            sx={{
              "& .MuiSelect-select": {
                backgroundColor: "#f5f5f5",
                "&:hover": {
                  backgroundColor: "#eeeeee",
                },
                "&.Mui-focused": {
                  backgroundColor: "#ffffff",
                },
              },
            }}
          >
            {dataTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "space-between",
            mt: 2,
          }}
        >
          <Button
            onClick={onCancel}
            variant="outlined"
            disabled={isSubmitting || loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={
              isSubmitting ||
              loading ||
              !formData.project_name.trim() ||
              !formData.data_type
            }
          >
            {submitButtonText}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectForm;
